import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from "utils/api";
import Pagination from "components/pagination";
import {
  MdArrowBack,
  MdPerson,
  MdEmail,
  MdPhone,
  MdShoppingBag,
  MdPayment,
  MdReceipt,
} from "react-icons/md";

const CUSTOMERS_PAGE_SIZE = 10;
const ORDERS_PAGE_SIZE = 5;
const TRANSACTIONS_PAGE_SIZE = 5;

const SectionCard = ({ title, icon: Icon, children }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
    <div className="mb-4 flex items-center gap-2 border-b border-gray-100 pb-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-base font-bold text-gray-900">{title}</h3>
    </div>
    {children}
  </div>
);

const InfoCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-600">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
        <p className="truncate text-sm font-semibold text-gray-900">{value ?? "—"}</p>
      </div>
    </div>
  </div>
);

export default function UsersFormView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [customersPage, setCustomersPage] = useState(1);

  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);
  const [txPage, setTxPage] = useState(1);

  const isDetailView = Boolean(id);

  const filteredCustomers = (customers || []).filter(
    (c) =>
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.mobile_no + "").includes(searchTerm)
  );
  const customersTotalPages = Math.max(1, Math.ceil(filteredCustomers.length / CUSTOMERS_PAGE_SIZE));
  const paginatedCustomers = filteredCustomers.slice(
    (customersPage - 1) * CUSTOMERS_PAGE_SIZE,
    customersPage * CUSTOMERS_PAGE_SIZE
  );

  const loadCustomers = async () => {
    try {
      const res = await api.post("/list_user", { role: "U" });
      setCustomers(res.data || []);
    } catch (err) {
      toast.error("Failed to load customers");
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoadingDetail(true);
    const load = async () => {
      try {
        const [userRes, bookingsRes, txRes] = await Promise.all([
          api.post("/list_single_user", { user_id: id }),
          api.post("/single_booking", { users_details: id }).catch(() => ({ data: [] })),
          api.post("/transaction_user", { user_details: id }).catch(() => ({ data: [] })),
        ]);
        setUser(Array.isArray(userRes.data) ? userRes.data[0] : userRes.data);
        setBookings(bookingsRes.data || []);
        setTransactions(txRes.data || []);
      } catch (err) {
        toast.error("Failed to load customer details");
        setUser(null);
        setBookings([]);
        setTransactions([]);
      } finally {
        setLoadingDetail(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    setCustomersPage(1);
  }, [searchTerm]);

  const totalSpent = (transactions || []).reduce((sum, t) => sum + (Number(t.bill_amount) || 0), 0);
  const ordersTotalPages = Math.max(1, Math.ceil(bookings.length / ORDERS_PAGE_SIZE));
  const paginatedBookings = bookings.slice(
    (ordersPage - 1) * ORDERS_PAGE_SIZE,
    ordersPage * ORDERS_PAGE_SIZE
  );
  const txTotalPages = Math.max(1, Math.ceil(transactions.length / TRANSACTIONS_PAGE_SIZE));
  const paginatedTx = transactions.slice(
    (txPage - 1) * TRANSACTIONS_PAGE_SIZE,
    txPage * TRANSACTIONS_PAGE_SIZE
  );

  const downloadInvoice = async (booking_id) => {
    try {
      const res = await api.get(`/admin/booking/${booking_id}/invoice`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice_${booking_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch {
      toast.error("Failed to download invoice");
    }
  };

  if (isDetailView) {
    if (loadingDetail) {
      return (
        <div className="flex min-h-[40vh] items-center justify-center p-6">
          <div className="flex flex-col items-center gap-3">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
            <p className="text-sm font-medium text-gray-500">Loading customer...</p>
          </div>
        </div>
      );
    }
    if (!user) {
      return (
        <div className="p-6">
          <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-center">
            <p className="font-medium text-red-800">Customer not found.</p>
            <button
              onClick={() => navigate("/admin/user-details")}
              className="mt-3 text-sm font-medium text-red-600 underline hover:no-underline"
            >
              Back to Customers
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-w-0 space-y-6 p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin/user-details")}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
              aria-label="Back to Customers"
            >
              <MdArrowBack className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Customer Profile</h1>
              <p className="text-sm text-gray-500">{user.name}</p>
            </div>
          </div>
          <span
            className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold ${
              user.status === 1 ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-rose-100 text-rose-800 border-rose-200"
            }`}
          >
            {user.status === 1 ? "Active" : "Inactive"}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <InfoCard label="Name" value={user.name} icon={MdPerson} />
          <InfoCard label="Email" value={user.email} icon={MdEmail} />
          <InfoCard label="Phone" value={user.mobile_no} icon={MdPhone} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <MdShoppingBag className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{bookings.length}</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                <MdPayment className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Total Spent</p>
                <p className="text-2xl font-bold text-gray-900">
                  ₹{totalSpent.toLocaleString("en-IN")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <SectionCard title="Orders" icon={MdReceipt}>
          {bookings.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">No orders yet.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <th className="py-2.5 px-2">Order</th>
                      <th className="py-2.5 px-2">Service</th>
                      <th className="py-2.5 px-2">Pickup</th>
                      <th className="py-2.5 px-2">Drop</th>
                      <th className="py-2.5 px-2">Status</th>
                      <th className="py-2.5 px-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedBookings.map((b) => (
                      <tr key={b.id} className="border-b hover:bg-gray-50">
                        <td className="py-2.5 px-2 font-medium">{b.ref_id || `#${b.id}`}</td>
                        <td className="py-2.5 px-2">
                          {b.booking_service_details?.[0]?.service_details?.name ||
                            b.booking_service_details?.[0]?.service_details?.service_name ||
                            "—"}
                        </td>
                        <td className="py-2.5 px-2">{b.pickup_date} {b.pickup_time}</td>
                        <td className="py-2.5 px-2">{b.drop_date} {b.drop_time}</td>
                        <td className="py-2.5 px-2">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                            b.status === "Delivered" ? "bg-emerald-100 text-emerald-800" :
                            b.status === "Pending" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"
                          }`}>
                            {b.status}
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-right">
                          <button
                            onClick={() => navigate(`/admin/orders/${b.id}`)}
                            className="text-sm font-medium text-blue-600 hover:underline"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {ordersTotalPages > 1 && (
                <Pagination
                  currentPage={ordersPage}
                  totalPages={ordersTotalPages}
                  totalItems={bookings.length}
                  pageSize={ORDERS_PAGE_SIZE}
                  onPageChange={setOrdersPage}
                  itemName="orders"
                />
              )}
            </>
          )}
        </SectionCard>

        <SectionCard title="Transactions" icon={MdPayment}>
          {transactions.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">No transactions yet.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <th className="py-2.5 px-2">Order ID</th>
                      <th className="py-2.5 px-2">Amount</th>
                      <th className="py-2.5 px-2">Date</th>
                      <th className="py-2.5 px-2">Method</th>
                      <th className="py-2.5 px-2">Status</th>
                      <th className="py-2.5 px-2 text-right">Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTx.map((p) => (
                      <tr key={p.id} className="border-b hover:bg-gray-50">
                        <td className="py-2.5 px-2 font-medium">{p.order_id}</td>
                        <td className="py-2.5 px-2">₹{Number(p.bill_amount || 0).toLocaleString("en-IN")}</td>
                        <td className="py-2.5 px-2">{p.payment_date}</td>
                        <td className="py-2.5 px-2">
                          {p.payment_mode === 1 && "UPI"}
                          {p.payment_mode === 2 && "Net Banking"}
                          {p.payment_mode === 3 && "Card"}
                          {p.payment_mode === 5 && "Cash on Delivery"}
                          {![1, 2, 3, 5].includes(p.payment_mode) && "—"}
                        </td>
                        <td className="py-2.5 px-2">
                          <span className={p.status === 1 ? "text-emerald-600 font-medium" : "text-gray-500"}>
                            {p.status === 1 ? "Success" : "Failed"}
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-right">
                          <button
                            onClick={() => downloadInvoice(p.booking_id)}
                            disabled={p.status !== 1}
                            className={`text-sm font-medium ${p.status === 1 ? "text-blue-600 hover:underline" : "text-gray-400 cursor-not-allowed"}`}
                          >
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {txTotalPages > 1 && (
                <Pagination
                  currentPage={txPage}
                  totalPages={txTotalPages}
                  totalItems={transactions.length}
                  pageSize={TRANSACTIONS_PAGE_SIZE}
                  onPageChange={setTxPage}
                  itemName="transactions"
                />
              )}
            </>
          )}
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-6 p-4 sm:p-6">
      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Customers</h1>
            <p className="mt-1 text-sm text-gray-500">Manage and view customer data and orders</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Search by name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:w-64"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="py-3 px-2">Name</th>
                <th className="py-3 px-2">Email</th>
                <th className="py-3 px-2">Phone</th>
                <th className="py-3 px-2">Status</th>
                <th className="py-3 px-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-gray-500">
                    No customers found
                  </td>
                </tr>
              ) : (
                paginatedCustomers.map((c) => (
                  <tr key={c.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-2 font-medium text-gray-900">{c.name}</td>
                    <td className="py-3 px-2 text-gray-600">{c.email}</td>
                    <td className="py-3 px-2 text-gray-600">{c.mobile_no ?? "—"}</td>
                    <td className="py-3 px-2">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          c.status === 1 ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {c.status === 1 ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <button
                        onClick={() => navigate(`/admin/user-details/${c.id}`)}
                        className="rounded-lg bg-blue-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-600 transition"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={customersPage}
          totalPages={customersTotalPages}
          totalItems={filteredCustomers.length}
          pageSize={CUSTOMERS_PAGE_SIZE}
          onPageChange={setCustomersPage}
          itemName="customers"
        />
      </div>
    </div>
  );
}
